import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Pencil, Trash2, Plus } from "lucide-react";
import { useEventTypes, useCreateEventType, useUpdateEventType, useDeleteEventType } from "@/hooks/usePackages";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface EventTypeManagementSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EventTypeManagementSheet({ open, onOpenChange }: EventTypeManagementSheetProps) {
  const { data: eventTypes } = useEventTypes();
  const createEventType = useCreateEventType();
  const updateEventType = useUpdateEventType();
  const deleteEventType = useDeleteEventType();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const startAdd = () => {
    setEditingId(null);
    setFormName("");
    setFormDescription("");
    setIsAdding(true);
  };

  const startEdit = (et: { id: string; name: string; description: string | null }) => {
    setIsAdding(false);
    setEditingId(et.id);
    setFormName(et.name);
    setFormDescription(et.description ?? "");
  };

  const cancelForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormName("");
    setFormDescription("");
  };

  const handleSave = () => {
    if (!formName.trim()) return;
    if (isAdding) {
      createEventType.mutate(
        { name: formName.trim(), description: formDescription.trim() || undefined },
        { onSuccess: cancelForm }
      );
    } else if (editingId) {
      updateEventType.mutate(
        { id: editingId, name: formName.trim(), description: formDescription.trim() || undefined },
        { onSuccess: cancelForm }
      );
    }
  };

  const sorted = eventTypes ? [...eventTypes].sort((a, b) => a.name.localeCompare(b.name)) : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Manage Event Types</SheetTitle>
          <SheetDescription>
            Add, edit, or remove event types used by packages. {sorted.length} event types total.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {(isAdding || editingId) && (
            <div className="space-y-3 rounded-lg border border-primary/20 bg-muted/30 p-4">
              <p className="text-sm font-medium">{isAdding ? "New Event Type" : "Edit Event Type"}</p>
              <div className="space-y-2">
                <Label className="text-xs">Name</Label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Wedding"
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Description (optional)</Label>
                <Input
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Brief description..."
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} disabled={!formName.trim() || createEventType.isPending || updateEventType.isPending}>
                  {isAdding ? "Add" : "Save"}
                </Button>
                <Button size="sm" variant="ghost" onClick={cancelForm}>Cancel</Button>
              </div>
            </div>
          )}

          {!isAdding && !editingId && (
            <Button variant="outline" size="sm" className="w-full" onClick={startAdd}>
              <Plus className="h-4 w-4 mr-2" /> Add Event Type
            </Button>
          )}

          <Separator />

          <div className="space-y-1">
            {sorted.map((et) => {
              const isEditing = editingId === et.id;
              return (
                <div
                  key={et.id}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 group transition-colors ${isEditing ? "bg-accent" : "hover:bg-muted/50"}`}
                >
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate">{et.name}</span>
                    {et.description && (
                      <p className="text-xs text-muted-foreground truncate">{et.description}</p>
                    )}
                  </div>

                  <Switch
                    checked={et.is_active}
                    onCheckedChange={(checked) => updateEventType.mutate({ id: et.id, is_active: checked })}
                    className="shrink-0"
                  />
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100" onClick={() => startEdit(et)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100">
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{et.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will remove the event type from all packages that use it. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteEventType.mutate(et.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              );
            })}

            {sorted.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">
                No event types yet. Add one to categorize your packages.
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
